import { Button, Checkbox, FormField, Input, SelectField, TextareaField } from "@makula/ui";

type ColorSwatch = { name: string; var: string; hex: string; textDark?: boolean };
type SwatchGroup = { label: string; swatches: ColorSwatch[] };

const primitiveGroups: SwatchGroup[] = [
  {
    label: "Blue",
    swatches: [
      { name: "Blue/10",  var: "--primitive-blue-10",  hex: "#E6E8FE", textDark: true },
      { name: "Blue/20",  var: "--primitive-blue-20",  hex: "#CDD1FE", textDark: true },
      { name: "Blue/40",  var: "--primitive-blue-40",  hex: "#9AA3FD", textDark: true },
      { name: "Blue/60",  var: "--primitive-blue-60",  hex: "#677AFB" },
      { name: "Blue/80",  var: "--primitive-blue-80",  hex: "#3451FA" },
      { name: "Blue/100", var: "--primitive-blue-100", hex: "#0517F8" },
    ],
  },
  {
    label: "Grey",
    swatches: [
      { name: "Grey/50",  var: "--primitive-grey-50",  hex: "#F3F4F6", textDark: true },
      { name: "Grey/10",  var: "--primitive-grey-10",  hex: "#E8EAED", textDark: true },
      { name: "Grey/20",  var: "--primitive-grey-20",  hex: "#D1D5DC", textDark: true },
      { name: "Grey/30",  var: "--primitive-grey-30",  hex: "#B0B8C6", textDark: true },
      { name: "Grey/40",  var: "--primitive-grey-40",  hex: "#8F9AAB", textDark: true },
      { name: "Grey/60",  var: "--primitive-grey-60",  hex: "#748094" },
      { name: "Grey/70",  var: "--primitive-grey-70",  hex: "#5A6880" },
      { name: "Grey/80",  var: "--primitive-grey-80",  hex: "#404F6B" },
      { name: "Grey/90",  var: "--primitive-grey-90",  hex: "#293B58" },
      { name: "Grey/100", var: "--primitive-grey-100", hex: "#172B4D" },
    ],
  },
  {
    label: "Red",
    swatches: [
      { name: "Red/10",  var: "--primitive-red-10",  hex: "#FFDCE8", textDark: true },
      { name: "Red/20",  var: "--primitive-red-20",  hex: "#FFB3CC", textDark: true },
      { name: "Red/30",  var: "--primitive-red-30",  hex: "#FF80AB", textDark: true },
      { name: "Red/40",  var: "--primitive-red-40",  hex: "#FF4D8A" },
      { name: "Red/50",  var: "--primitive-red-50",  hex: "#EE0064" },
      { name: "Red/100", var: "--primitive-red-100", hex: "#B5004C" },
    ],
  },
  {
    label: "Green",
    swatches: [
      { name: "Green/10",  var: "--primitive-green-10",  hex: "#E6F9F1", textDark: true },
      { name: "Green/20",  var: "--primitive-green-20",  hex: "#BBF0D9", textDark: true },
      { name: "Green/50",  var: "--primitive-green-50",  hex: "#10B981" },
      { name: "Green/100", var: "--primitive-green-100", hex: "#065F46" },
    ],
  },
  {
    label: "Orange",
    swatches: [
      { name: "Orange/10",  var: "--primitive-orange-10",  hex: "#FFF0E6", textDark: true },
      { name: "Orange/20",  var: "--primitive-orange-20",  hex: "#FFDAB3", textDark: true },
      { name: "Orange/50",  var: "--primitive-orange-50",  hex: "#FF7A00" },
      { name: "Orange/100", var: "--primitive-orange-100", hex: "#C25100" },
    ],
  },
  {
    label: "Pink",
    swatches: [
      { name: "Pink/200", var: "--primitive-pink-200", hex: "#F9A8D4", textDark: true },
      { name: "Pink/300", var: "--primitive-pink-300", hex: "#F472B6", textDark: true },
      { name: "Pink/400", var: "--primitive-pink-400", hex: "#EC4899" },
    ],
  },
];

type SemanticRow = { token: string; cssVar: string; value: string; swatch?: string };
type SemanticSection = { label: string; rows: SemanticRow[] };

const semanticSections: SemanticSection[] = [
  {
    label: "Content",
    rows: [
      { token: "content-primary",   cssVar: "--color-content-primary",   value: "#172B4D", swatch: "#172B4D" },
      { token: "content-secondary", cssVar: "--color-content-secondary", value: "#748094", swatch: "#748094" },
      { token: "content-tertiary",  cssVar: "--color-content-tertiary",  value: "#A2AAB8", swatch: "#A2AAB8" },
      { token: "content-inverse",   cssVar: "--color-content-inverse",   value: "#FFFFFF",  swatch: "#FFFFFF" },
      { token: "content-brand",     cssVar: "--color-content-brand",     value: "#0517F8", swatch: "#0517F8" },
      { token: "content-danger",    cssVar: "--color-content-danger",    value: "#EE0064", swatch: "#EE0064" },
      { token: "content-warning",   cssVar: "--color-content-warning",   value: "#FF7A00", swatch: "#FF7A00" },
      { token: "content-success",   cssVar: "--color-content-success",   value: "#10B981", swatch: "#10B981" },
      { token: "content-disabled",  cssVar: "--color-content-disabled",  value: "#A2AAB8", swatch: "#A2AAB8" },
    ],
  },
  {
    label: "Background",
    rows: [
      { token: "bg-primary",        cssVar: "--color-bg-primary",        value: "#FFFFFF",  swatch: "#FFFFFF" },
      { token: "bg-secondary",      cssVar: "--color-bg-secondary",      value: "#172B4D", swatch: "#172B4D" },
      { token: "bg-accent",         cssVar: "--color-bg-accent",         value: "#F3F4F6", swatch: "#F3F4F6" },
      { token: "bg-brand-dark",     cssVar: "--color-bg-brand-dark",     value: "#0517F8", swatch: "#0517F8" },
      { token: "bg-brand-lightest", cssVar: "--color-bg-brand-lightest", value: "#E6E8FE", swatch: "#E6E8FE" },
      { token: "bg-danger",         cssVar: "--color-bg-danger",         value: "#EE0064", swatch: "#EE0064" },
      { token: "bg-danger-light",   cssVar: "--color-bg-danger-light",   value: "#FFDCE8", swatch: "#FFDCE8" },
      { token: "bg-warning",        cssVar: "--color-bg-warning",        value: "#FF7A00", swatch: "#FF7A00" },
      { token: "bg-warning-light",  cssVar: "--color-bg-warning-light",  value: "#FFF0E6", swatch: "#FFF0E6" },
      { token: "bg-success",        cssVar: "--color-bg-success",        value: "#10B981", swatch: "#10B981" },
      { token: "bg-success-light",  cssVar: "--color-bg-success-light",  value: "#E6F9F1", swatch: "#E6F9F1" },
    ],
  },
  {
    label: "Border",
    rows: [
      { token: "border-primary",   cssVar: "--color-border-primary",   value: "#E8EAED", swatch: "#E8EAED" },
      { token: "border-secondary", cssVar: "--color-border-secondary", value: "#D1D5DC", swatch: "#D1D5DC" },
      { token: "border-brand",     cssVar: "--color-border-brand",     value: "#0517F8", swatch: "#0517F8" },
      { token: "border-danger",    cssVar: "--color-border-danger",    value: "#EE0064", swatch: "#EE0064" },
    ],
  },
];

const typographyStyles = [
  { name: "Heading / 2XL", size: "3rem",     weight: "700", lh: "3.5rem",   sample: "The quick brown fox" },
  { name: "Heading / XL",  size: "2.25rem",  weight: "700", lh: "2.5rem",   sample: "The quick brown fox" },
  { name: "Heading / L",   size: "1.75rem",  weight: "700", lh: "2.25rem",  sample: "The quick brown fox" },
  { name: "Heading / M",   size: "1.5rem",   weight: "700", lh: "2rem",     sample: "The quick brown fox" },
  { name: "Heading / S",   size: "1.25rem",  weight: "700", lh: "1.75rem",  sample: "The quick brown fox" },
  { name: "Heading / XS",  size: "1.125rem", weight: "700", lh: "1.5rem",   sample: "The quick brown fox" },
  { name: "Label / L",     size: "1rem",     weight: "700", lh: "1.5rem",   sample: "Label text example" },
  { name: "Label / M",     size: "0.875rem", weight: "700", lh: "1.25rem",  sample: "Label text example" },
  { name: "Label / S",     size: "0.75rem",  weight: "700", lh: "1rem",     sample: "Label text example" },
  { name: "Label / XS",    size: "0.625rem", weight: "700", lh: "0.875rem", sample: "Label text example" },
  { name: "Paragraph / L", size: "1rem",     weight: "500", lh: "1.5rem",   sample: "Paragraph body text for reading" },
  { name: "Paragraph / S", size: "0.875rem", weight: "500", lh: "1.25rem",  sample: "Paragraph body text for reading" },
  { name: "Paragraph / XS",size: "0.75rem",  weight: "500", lh: "1rem",     sample: "Paragraph body text for reading" },
];

const spacingTokens = [
  { name: "3XS", var: "--spacing-3xs", px: "2px" },
  { name: "2XS", var: "--spacing-2xs", px: "4px" },
  { name: "XS",  var: "--spacing-xs",  px: "6px" },
  { name: "S",   var: "--spacing-s",   px: "8px" },
  { name: "M",   var: "--spacing-m",   px: "12px" },
  { name: "L",   var: "--spacing-l",   px: "16px" },
  { name: "XL",  var: "--spacing-xl",  px: "20px" },
  { name: "2XL", var: "--spacing-2xl", px: "24px" },
  { name: "3XL", var: "--spacing-3xl", px: "32px" },
  { name: "4XL", var: "--spacing-4xl", px: "40px" },
  { name: "5XL", var: "--spacing-5xl", px: "48px" },
  { name: "6XL", var: "--spacing-6xl", px: "64px" },
];

const radiusTokens = [
  { name: "XS",   var: "--radius-xs",   px: "6px",    preview: "0.375rem" },
  { name: "S",    var: "--radius-s",    px: "8px",    preview: "0.5rem" },
  { name: "M",    var: "--radius-m",    px: "12px",   preview: "0.75rem" },
  { name: "L",    var: "--radius-l",    px: "16px",   preview: "1rem" },
  { name: "XL",   var: "--radius-xl",   px: "24px",   preview: "1.5rem" },
  { name: "Full", var: "--radius-full", px: "9999px", preview: "9999px" },
];

const elevationTokens = [
  { name: "XS", var: "--elevation-xs", shadow: "0 1px 2px rgba(23,43,77,0.06)" },
  { name: "S",  var: "--elevation-s",  shadow: "0 2px 4px rgba(23,43,77,0.08)" },
  { name: "M",  var: "--elevation-m",  shadow: "0 8px 24px rgba(23,43,77,0.08), 0 0 2px 1px rgba(23,43,77,0.04)" },
  { name: "L",  var: "--elevation-l",  shadow: "0 16px 48px rgba(23,43,77,0.12), 0 0 4px 1px rgba(23,43,77,0.06)" },
  { name: "XL", var: "--elevation-xl", shadow: "0 24px 64px rgba(23,43,77,0.16), 0 0 8px 2px rgba(23,43,77,0.08)" },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: "3rem" }}>
      <h2 style={{
        fontFamily: "var(--font-family-base)",
        fontSize: "var(--font-size-heading-m)",
        fontWeight: "var(--font-weight-bold)",
        color: "var(--color-content-primary)",
        lineHeight: "var(--line-height-heading-m)",
        letterSpacing: "var(--letter-spacing-tight)",
        marginBottom: "1.5rem",
        paddingBottom: "0.75rem",
        borderBottom: "1px solid var(--color-border-primary)",
      }}>{title}</h2>
      {children}
    </section>
  );
}

function Chip({ label }: { label: string }) {
  return (
    <span style={{
      display: "inline-block",
      fontSize: "var(--font-size-label-xs)",
      fontWeight: "var(--font-weight-bold)",
      fontFamily: "var(--font-family-base)",
      background: "var(--color-bg-brand-lightest)",
      color: "var(--color-content-brand)",
      padding: "2px 6px",
      borderRadius: "var(--radius-xs)",
      letterSpacing: "0.02em",
    }}>{label}</span>
  );
}

export default function FoundationsPage() {
  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--color-bg-primary)",
      fontFamily: "var(--font-family-base)",
    }}>
      {/* Header */}
      <header style={{
        background: "var(--color-bg-secondary)",
        padding: "2rem 3rem",
        display: "flex",
        alignItems: "center",
        gap: "1rem",
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: "var(--font-size-heading-xl)",
            fontWeight: "var(--font-weight-bold)",
            color: "var(--color-content-inverse)",
            lineHeight: "var(--line-height-heading-xl)",
            letterSpacing: "var(--letter-spacing-tight)",
          }}>
            Makula Design System
          </h1>
          <p style={{
            margin: "0.25rem 0 0",
            fontSize: "var(--font-size-paragraph-s)",
            color: "var(--color-content-tertiary)",
            fontWeight: "var(--font-weight-medium)",
          }}>
            Foundations · Primitives · Semantics · Typography · Spacing · Elevation
          </p>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <Chip label="v0.1.0" />
        </div>
      </header>

      <main style={{ maxWidth: "1200px", margin: "0 auto", padding: "3rem 2rem" }}>

        {/* ── Primitive Colors ──────────────────────────────────────────── */}
        <Section title="Color Primitives">
          {primitiveGroups.map((group) => (
            <div key={group.label} style={{ marginBottom: "2rem" }}>
              <p style={{
                fontSize: "var(--font-size-label-s)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-content-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: "0.75rem",
              }}>{group.label}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {group.swatches.map((s) => (
                  <div key={s.name} style={{
                    width: "100px",
                    borderRadius: "var(--radius-s)",
                    overflow: "hidden",
                    boxShadow: "var(--elevation-xs)",
                    background: "#fff",
                  }}>
                    <div style={{
                      height: "56px",
                      background: s.hex,
                      border: s.hex === "#FFFFFF" ? "1px solid var(--color-border-primary)" : "none",
                    }} />
                    <div style={{ padding: "6px 8px" }}>
                      <p style={{
                        margin: 0,
                        fontSize: "var(--font-size-label-xs)",
                        fontWeight: "var(--font-weight-bold)",
                        color: "var(--color-content-primary)",
                      }}>{s.name}</p>
                      <p style={{
                        margin: "2px 0 0",
                        fontSize: "0.6rem",
                        color: "var(--color-content-tertiary)",
                        fontFamily: "monospace",
                      }}>{s.hex}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        {/* ── Semantic Tokens ───────────────────────────────────────────── */}
        <Section title="Semantic Tokens">
          {semanticSections.map((section) => (
            <div key={section.label} style={{ marginBottom: "2rem" }}>
              <p style={{
                fontSize: "var(--font-size-label-s)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-content-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: "0.75rem",
              }}>{section.label}</p>
              <div style={{
                background: "var(--color-bg-primary)",
                borderRadius: "var(--radius-m)",
                border: "1px solid var(--color-border-primary)",
                overflow: "hidden",
              }}>
                {section.rows.map((row, i) => (
                  <div key={row.token} style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr 1fr 1fr",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "0.625rem 1rem",
                    borderTop: i > 0 ? "1px solid var(--color-border-primary)" : "none",
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "var(--radius-xs)",
                      background: row.swatch,
                      border: row.swatch === "#FFFFFF" ? "1px solid var(--color-border-primary)" : "none",
                      flexShrink: 0,
                    }} />
                    <code style={{ fontSize: "0.75rem", color: "var(--color-content-brand)", fontFamily: "monospace" }}>
                      {row.cssVar}
                    </code>
                    <span style={{ fontSize: "var(--font-size-label-s)", color: "var(--color-content-primary)" }}>
                      {row.token}
                    </span>
                    <code style={{ fontSize: "0.7rem", color: "var(--color-content-tertiary)", fontFamily: "monospace" }}>
                      {row.value}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Section>

        {/* ── Typography ────────────────────────────────────────────────── */}
        <Section title="Typography — Manrope">
          <div style={{
            background: "var(--color-bg-primary)",
            borderRadius: "var(--radius-m)",
            border: "1px solid var(--color-border-primary)",
            overflow: "hidden",
          }}>
            {typographyStyles.map((t, i) => (
              <div key={t.name} style={{
                display: "grid",
                gridTemplateColumns: "160px 1fr auto",
                alignItems: "center",
                gap: "1.5rem",
                padding: "1rem 1.5rem",
                borderTop: i > 0 ? "1px solid var(--color-border-primary)" : "none",
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: "var(--font-size-label-xs)", fontWeight: "var(--font-weight-bold)", color: "var(--color-content-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {t.name}
                  </p>
                  <p style={{ margin: "4px 0 0", fontSize: "0.65rem", color: "var(--color-content-tertiary)", fontFamily: "monospace" }}>
                    {t.size} / {t.weight} / lh {t.lh}
                  </p>
                </div>
                <p style={{
                  margin: 0,
                  fontFamily: "var(--font-family-base)",
                  fontSize: t.size,
                  fontWeight: t.weight,
                  lineHeight: t.lh,
                  color: "var(--color-content-primary)",
                  letterSpacing: parseInt(t.weight) >= 700 ? "var(--letter-spacing-tight)" : "var(--letter-spacing-normal)",
                }}>
                  {t.sample}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Spacing ───────────────────────────────────────────────────── */}
        <Section title="Spacing">
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {spacingTokens.map((t) => (
              <div key={t.name} style={{
                display: "grid",
                gridTemplateColumns: "80px 180px 1fr",
                alignItems: "center",
                gap: "1rem",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "var(--font-size-label-s)", fontWeight: "var(--font-weight-bold)", color: "var(--color-content-secondary)" }}>
                    {t.name}
                  </span>
                  <span style={{ fontSize: "0.65rem", color: "var(--color-content-tertiary)", fontFamily: "monospace" }}>
                    {t.px}
                  </span>
                </div>
                <code style={{ fontSize: "0.7rem", color: "var(--color-content-brand)", fontFamily: "monospace" }}>
                  {t.var}
                </code>
                <div style={{
                  height: "8px",
                  width: t.px,
                  background: "var(--color-bg-brand-dark)",
                  borderRadius: "var(--radius-full)",
                  minWidth: "4px",
                }} />
              </div>
            ))}
          </div>
        </Section>

        {/* ── Radius ────────────────────────────────────────────────────── */}
        <Section title="Border Radius">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            {radiusTokens.map((t) => (
              <div key={t.name} style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.5rem",
              }}>
                <div style={{
                  width: "64px",
                  height: "64px",
                  background: "var(--color-bg-brand-lightest)",
                  border: "2px solid var(--color-border-brand)",
                  borderRadius: t.preview,
                }} />
                <p style={{ margin: 0, fontSize: "var(--font-size-label-xs)", fontWeight: "var(--font-weight-bold)", color: "var(--color-content-primary)" }}>
                  {t.name}
                </p>
                <p style={{ margin: 0, fontSize: "0.6rem", color: "var(--color-content-tertiary)", fontFamily: "monospace" }}>
                  {t.px}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Buttons ───────────────────────────────────────────────────── */}
        <Section title="Components / Buttons">
          {(["primary", "secondary", "outline", "danger", "ghost"] as const).map((variant) => (
            <div key={variant} style={{ marginBottom: "1.5rem" }}>
              <p style={{
                margin: "0 0 0.75rem",
                fontSize: "var(--font-size-label-s)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-content-secondary)",
                textTransform: "capitalize",
                letterSpacing: "0.04em",
              }}>{variant}</p>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.75rem" }}>
                <Button variant={variant} size="lg">Label</Button>
                <Button variant={variant} size="sm">Label</Button>
                <Button variant={variant} size="lg" leadingIcon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/></svg>}>Label</Button>
                <Button variant={variant} size="lg" trailingIcon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}>Label</Button>
                <Button variant={variant} size="lg" iconOnly><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/></svg></Button>
                <Button variant={variant} size="lg" disabled>Disabled</Button>
              </div>
            </div>
          ))}
        </Section>

        {/* ── Inputs ────────────────────────────────────────────────────── */}
        <Section title="Components / Input Fields">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "2rem" }}>
            <Input label="Default" placeholder="Placeholder text" description="Helper text describing the field." required />
            <Input label="Filled" defaultValue="Some value" description="Helper text describing the field." required />
            <Input label="Focused (click me)" placeholder="Click to focus" description="Helper text describing the field." required />
            <Input label="Error state" defaultValue="Wrong value" error="This field has an error message." required />
            <Input label="Disabled" placeholder="Cannot edit" description="This field is disabled." disabled required />
            <Input
              label="With leading icon"
              placeholder="Search..."
              description="Helper text."
              leadingIcon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            />
            <Input
              label="With trailing icon"
              placeholder="Select..."
              description="Helper text."
              trailingIcon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            />
            <Input
              label="With counter"
              placeholder="Type something..."
              description="Max 50 characters."
              showCounter
              maxLength={50}
            />
            <Input
              label="Error with counter"
              defaultValue="Too long input value"
              error="Exceeds the maximum length."
              showCounter
              maxLength={20}
              required
            />
          </div>
        </Section>

        {/* ── Textarea Fields ───────────────────────────────────────────── */}
        <Section title="Components / Textarea Fields">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "2rem" }}>
            <TextareaField label="Default" placeholder="Type something..." description="Helper text describing the field." required />
            <TextareaField label="Filled" defaultValue="This is some filled content in the textarea field." description="Helper text describing the field." required />
            <TextareaField label="Error state" defaultValue="Invalid content" error="This field has an error." required />
            <TextareaField label="Disabled" placeholder="Cannot edit" description="This field is disabled." disabled />
          </div>
        </Section>

        {/* ── Amount / Badge Fields ──────────────────────────────────────── */}
        <Section title="Components / Input Fields with Badge">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "2rem" }}>
            <FormField label="Amount" leadingBadge="EUR" type="number" placeholder="0.00" required />
            <FormField label="Weight" leadingBadge="KG" type="number" placeholder="0.00" />
            <FormField label="Percentage" leadingBadge="%" type="number" placeholder="0" />
            <FormField label="Amount (error)" leadingBadge="EUR" defaultValue="-5" error="Amount must be positive." required />
          </div>
        </Section>

        {/* ── Select Fields ─────────────────────────────────────────────── */}
        <Section title="Components / Select Fields">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "2rem" }}>
            <SelectField
              label="Default"
              placeholder="Select an option"
              description="This is where we will write the description of the input field."
              required
              options={[
                { label: "Example Help Text", value: "example" },
                { label: "Option Two", value: "two" },
                { label: "Option Three", value: "three" },
              ]}
            />
            <SelectField
              label="Filled"
              defaultValue="example"
              description="This is where we will write the description of the input field."
              required
              options={[
                { label: "Example Help Text", value: "example" },
                { label: "Option Two", value: "two" },
                { label: "Option Three", value: "three" },
              ]}
            />
            <SelectField
              label="Error state"
              defaultValue=""
              error="Please select a valid option."
              required
              options={[
                { label: "Example Help Text", value: "example" },
                { label: "Option Two", value: "two" },
              ]}
            />
            <SelectField
              label="Disabled"
              placeholder="Cannot select"
              description="This field is disabled."
              disabled
              options={[
                { label: "Example Help Text", value: "example" },
                { label: "Option Two", value: "two" },
              ]}
            />
          </div>
        </Section>

        {/* ── Checkboxes ────────────────────────────────────────────────── */}
        <Section title="Components / Checkboxes">
          {(["sm", "md", "lg"] as const).map((size) => (
            <div key={size} style={{ marginBottom: "1.5rem" }}>
              <p style={{
                margin: "0 0 0.75rem",
                fontSize: "var(--font-size-label-s)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-content-secondary)",
                textTransform: "capitalize",
                letterSpacing: "0.04em",
              }}>{size}</p>
              <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                <Checkbox size={size} />
                <Checkbox size={size} defaultChecked />
                <Checkbox size={size} indeterminate />
                <Checkbox size={size} disabled />
                <Checkbox size={size} defaultChecked disabled />
                <Checkbox size={size} indeterminate disabled />
              </div>
            </div>
          ))}
        </Section>

        {/* ── Elevation ─────────────────────────────────────────────────── */}
        <Section title="Elevation">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2rem", alignItems: "flex-end" }}>
            {elevationTokens.map((t) => (
              <div key={t.name} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
                <div style={{
                  width: "96px",
                  height: "96px",
                  background: "var(--color-bg-primary)",
                  borderRadius: "var(--radius-m)",
                  boxShadow: t.shadow,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <span style={{ fontSize: "var(--font-size-label-m)", fontWeight: "var(--font-weight-bold)", color: "var(--color-content-secondary)" }}>
                    {t.name}
                  </span>
                </div>
                <code style={{ fontSize: "0.65rem", color: "var(--color-content-brand)", fontFamily: "monospace" }}>
                  {t.var}
                </code>
              </div>
            ))}
          </div>
        </Section>

      </main>
    </div>
  );
}
